import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Brain, Eye, MessageSquare, FileSearch, Image, Mic, 
  Languages, TrendingUp, Shield, Sparkles, ChevronRight
} from 'lucide-react';

const capabilities = [
  {
    id: 'vision',
    icon: Eye,
    title: 'Computer Vision',
    tagline: 'AI that sees and understands',
    examples: [
      'Read handwritten notes and convert to text instantly',
      'Scan products and auto-generate inventory listings',
      'Analyze construction site photos for safety issues',
      'Extract data from receipts, invoices, business cards'
    ],
    color: 'blue'
  },
  {
    id: 'language',
    icon: MessageSquare,
    title: 'Natural Language',
    tagline: 'AI that reads, writes, and communicates',
    examples: [
      'Draft professional emails in your writing style',
      'Summarize 50-page contracts in 30 seconds',
      'Answer customer questions 24/7 with context',
      'Generate product descriptions from bullet points'
    ],
    color: 'emerald'
  },
  {
    id: 'analysis',
    icon: TrendingUp,
    title: 'Predictive Analysis',
    tagline: 'AI that spots patterns humans miss',
    examples: [
      'Predict which customers are likely to churn',
      'Forecast inventory needs before stockouts',
      'Identify fraudulent transactions in real-time',
      'Optimize pricing based on demand patterns'
    ],
    color: 'amber'
  },
  {
    id: 'automation',
    icon: Brain,
    title: 'Intelligent Automation',
    tagline: 'AI that handles the repetitive work',
    examples: [
      'Route support tickets to the right department',
      'Auto-categorize expenses and transactions',
      'Schedule appointments based on availability',
      'Generate reports from multiple data sources'
    ],
    color: 'purple'
  }
];

const colorClasses = {
  blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', glow: 'shadow-blue-500/20' },
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', glow: 'shadow-emerald-500/20' },
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', glow: 'shadow-amber-500/20' },
  purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400', glow: 'shadow-purple-500/20' }
};

export default function AICapabilitiesShowcase() {
  const [activeId, setActiveId] = useState('vision');
  const active = capabilities.find(c => c.id === activeId);
  const colors = colorClasses[active.color];

  return (
    <section className="bg-slate-950 py-24 md:py-32 relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-950" />
      <motion.div 
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-3xl opacity-20 ${colors.bg}`}
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 4, repeat: Infinity }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-purple-400 text-sm font-semibold tracking-widest uppercase">
            What AI Can Really Do
          </span>
          <h2 className="mt-4 text-3xl md:text-5xl font-bold text-white tracking-tight">
            Capabilities most businesses don't know exist
          </h2>
          <p className="mt-4 text-slate-400 text-lg max-w-2xl mx-auto">
            This isn't science fiction. These are real AI capabilities available today — 
            and we can implement them for your business.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Capability Selector */}
          <div className="lg:col-span-2 space-y-3">
            {capabilities.map((cap) => {
              const isActive = cap.id === activeId;
              const capColors = colorClasses[cap.color];
              return (
                <motion.button
                  key={cap.id}
                  onClick={() => setActiveId(cap.id)}
                  className={`w-full text-left p-4 rounded-xl border transition-all duration-300 ${
                    isActive 
                      ? `${capColors.bg} ${capColors.border} shadow-lg ${capColors.glow}` 
                      : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'
                  }`}
                  whileHover={{ x: isActive ? 0 : 4 }}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${isActive ? capColors.bg : 'bg-slate-800'}`}>
                      <cap.icon className={`w-5 h-5 ${isActive ? capColors.text : 'text-slate-400'}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className={`font-semibold ${isActive ? 'text-white' : 'text-slate-300'}`}>
                        {cap.title}
                      </h3>
                      <p className={`text-sm ${isActive ? capColors.text : 'text-slate-500'}`}>
                        {cap.tagline}
                      </p>
                    </div>
                    <ChevronRight className={`w-5 h-5 transition-transform ${
                      isActive ? `${capColors.text} rotate-90` : 'text-slate-600'
                    }`} />
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* Examples Display */}
          <div className="lg:col-span-3">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className={`rounded-2xl ${colors.bg} border ${colors.border} p-8 h-full`}
              >
                <div className="flex items-center gap-3 mb-6">
                  <Sparkles className={`w-6 h-6 ${colors.text}`} />
                  <h3 className="text-xl font-bold text-white">Real-World Examples</h3>
                </div>
                <div className="space-y-4">
                  {active.examples.map((example, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="flex items-start gap-3 p-4 rounded-xl bg-slate-900/50 border border-slate-800"
                    >
                      <div className={`mt-1 w-2 h-2 rounded-full ${colors.text} bg-current`} />
                      <p className="text-slate-300">{example}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

